import { catchError, filter, from, map, mergeMap, of, switchMap, withLatestFrom } from 'rxjs';
import { ofType } from 'redux-observable';
import { selectMarketId } from 'features/searchResultsFacets';
import mapDataSlice from './dataSlice';
import mapInteractionSlice from './interactionSlice';
import getBuildablePlanSummaryCardsInformation from '../apis/getBuildablePlanSummaryCardsInformation';
import processBuildablePlansToCardMetadata from '../services/processBuildablePlans';
import { selectMapResults, selectSelectedMarker } from './selectors';
import MarkerType from '../enums/markerType';
import { mapMarkerToCardInformation } from '../services/mapper';

const dataActions = mapDataSlice.actions;
const interactionActions = mapInteractionSlice.actions;

/**
 * Epic that reacts to `fetchBuildablePlans`, calls the API, and dispatches
 * `setMarkerCards` with the mapped plan metadata.
 *
 * @param {Observable} action$ - Stream of dispatched Redux actions.
 * @param {StateObservable} state$ - Observable of the current Redux state.
 * @returns {Observable} Stream of actions to dispatch.
 */
const fetchBuildablePlansMiniCardsEpic = (action$, state$) =>
  action$.pipe(
    ofType(dataActions.fetchBuildablePlans.type),
    switchMap((action) => {
      const { communityId } = action.payload;
      const marketId = selectMarketId(state$.value);
      
      return getBuildablePlanSummaryCardsInformation(communityId, marketId).pipe(
        withLatestFrom(state$),
        // Guard: discard the response if the user deselected the marker while
        // the fetch was in flight — prevents stale card data rendering.
        filter(([, currentState]) => {
          const currentSelectedId = selectSelectedMarker(currentState)?.id;
          return currentSelectedId === communityId;
        }),
        map(([data, currentState]) => {
          const processedData = processBuildablePlansToCardMetadata(data, MarkerType.PlanUnit.toString());
          const community = selectMapResults(currentState)?.find(
            (result) => result.id === communityId && 
            result.type === MarkerType.BuildablePlan.toString()
          );
          return [
            dataActions.fetchBuildablePlansFulfilled(),
            interactionActions.setMarkerCards([mapMarkerToCardInformation(community), ...processedData])
          ];
        }),
        mergeMap((actionsToDispatch) => from(actionsToDispatch)),
        catchError((error) => of(dataActions.fetchBuildablePlansError(error))),
      );
    }),
  );

  export default fetchBuildablePlansMiniCardsEpic;